import React, { useEffect, useMemo } from 'react';
import { FormBuilder, FieldControl, Validators } from 'react-reactive-form';
import Select from 'react-select';
import AmountContainer from './AmountContainer';
import { positiveNumberValidator } from '../helpers/validator.helper';
import {
  trialPeriodDaysValidator,
  borrowersUnlimitedValidator,
  partnersUnlimitedValidator,
  usersUnlimitedValidator,
  transactionsUnlimitedValidator,
  creditReportUnlimitedValidator,
  thirdPartyReportsUnlimitedValidator,
  helloSignUnlimitedValidator,
  creditReportStatusValidator,
  thirdPartyReportsStatusValidator,
  helloSignStatusValidator,
} from '../helpers/subscriptionPlanValidators';
import moment from 'moment';
import { useAppContext } from '../contexts/AppContext';
import { Constants, limitDurationOptions, unlimitedLimitedOptions } from '../constants';
import { getNumber } from '../helpers/number.helper';
import { getBoolean } from '../helpers/index.helper';

const CreateCustomPlan = ({ onFormInit, data, disabled = false, showTrialEndDetail = true, isCustom = false, selectedPlan }) => {
  const { showToaster, hideToaster } = useAppContext();
  const customPlanForm = useMemo(
    () =>
      FormBuilder.group({
        PlanName: ['', [Validators.required, Validators.maxLength(50)]],
        Price: ['', [Validators.required, positiveNumberValidator]],
        AllowTrialPeriod: ['0', Validators.required],
        TrialPeriodDays: ['', trialPeriodDaysValidator],
        LimitDuration: ['1', Validators.required],
        NoofBorrowers: ['', Validators.required],
        NoofBorrowersLimit: ['', [borrowersUnlimitedValidator]],
        BorrowersOverCharge: ['', [borrowersUnlimitedValidator]],
        NoofPartners: ['', Validators.required],
        NoofPartnersLimit: ['', [partnersUnlimitedValidator]],
        PartnersOverCharge: ['', [partnersUnlimitedValidator]],
        NoofUsers: ['', Validators.required],
        NoofUsersLimit: ['', [usersUnlimitedValidator]],
        UsersOverCharge: ['', [usersUnlimitedValidator]],
        NoofTransactions: ['', Validators.required],
        NoofTransactionsLimit: ['', [transactionsUnlimitedValidator]],
        TransactionsOverCharge: ['', [transactionsUnlimitedValidator]],
        CreditReportStatus: ['0', Validators.required],
        CreditReport: ['', creditReportStatusValidator],
        CreditReportLimit: ['', [creditReportUnlimitedValidator]],
        CreditReportOverCharge: ['', [creditReportUnlimitedValidator]],
        ThirdPartyReportsStatus: ['0', Validators.required],
        ThirdPartyReports: ['', thirdPartyReportsStatusValidator],
        ThirdPartyReportsLimit: ['', [thirdPartyReportsUnlimitedValidator]],
        ThirdPartyReportsOverCharge: ['', [thirdPartyReportsUnlimitedValidator]],
        HelloSignStatus: ['0', Validators.required],
        HelloSign: ['', helloSignStatusValidator],
        HelloSignLimit: ['', [helloSignUnlimitedValidator]],
        HelloSignOverCharge: ['', [helloSignUnlimitedValidator]],
        APIName: ['microbit', [Validators.required]],
      }),
    []
  );

  useEffect(() => {
    if (data) {
      customPlanForm.patchValue(data);
    } else {
      customPlanForm.reset({
        PlanName: isCustom ? `Custom_${Date.now()}` : '',
        AllowTrialPeriod: '0',
        LimitDuration: '1',
        CreditReportStatus: '0',
        ThirdPartyReportsStatus: '0',
        HelloSignStatus: '0',
        APIName: 'microbit'
      });
    }
    customPlanForm.updateValueAndValidity();
  }, [data, disabled]);

  useEffect(() => {
    if (onFormInit) {
      onFormInit(customPlanForm);
    }
  }, [onFormInit, customPlanForm]);

  return (
    <div className="lm-card">
      <h4 className="lm-card-heading" style={{ marginBottom: '20px', fontSize: '16px' }}>
        {disabled ? 'Plan Details' : 'Create Custom Plan'}
      </h4>

      <div className="row">
        {
          !isCustom && !getBoolean(selectedPlan?.IsCustom) && (
            <div className="col-md-4">
              <FieldControl
                control={customPlanForm.get('PlanName')}
                render={({ value, setValue, touched, hasError }) => {
                  const showError = touched && hasError('required');
                  const showErrorMaxLength = touched && hasError('maxlength');
                  return (
                    <div className={`form-group ${showError ? 'error-field' : ''}`}>
                      <label htmlFor="plan_name">Plan Name<span className="asterisk">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="plan_name"
                        placeholder="Enter plan name"
                        value={value}
                        onChange={(e) => {
                          if (disabled) return;
                          setValue(e.target.value);
                        }}
                        maxLength={50}
                        disabled={disabled}
                      />
                      {showError && (
                        <span className="error-message">Plan name is required</span>
                      )}
                      {showErrorMaxLength && (
                        <span className="error-message">Plan name must be less than 50 characters</span>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          )
        }
        <div className="col-md-4">
          <AmountContainer
            name="Price"
            label="Price"
            placeholder="0.00"
            maxlength={11}
            removeDefaultPlaceholder={true}
            setMinValidation={true}
            control={customPlanForm.get('Price')}
            isRequired={true}
            className='m-0'
            errorMessage="Price is required"
            disabled={disabled}
          />
          <small style={{ fontSize: '12px', color: '#99A4B1' }}>Per Month</small>
        </div>
      </div>

      <div className="form-group mt-3">
        <div className="row mb-3 pl-1">
          <div className="col-md-2">
            <label className="mt-1" htmlFor="allow_trial_period">
              Allow Trial Period<span className="asterisk">*</span>
            </label>
          </div>
          <div className="col-md-1">
            <FieldControl
              strict={false}
              control={customPlanForm.get('AllowTrialPeriod')}
              render={({ value, setValue }) => (
                <div className="lm-toggle-switch">
                  <input
                    type="checkbox"
                    id="allow_trial_period"
                    checked={value === 1}
                    disabled={disabled}
                    onChange={(e) => {
                      if (disabled) return;
                      if (e.target.checked && getNumber(customPlanForm.value?.Price || "0") <= 0) {
                        showToaster('Price should be greater than 0 to enable trial period', 'error');
                        setTimeout(() => {
                          hideToaster();
                        }, Constants.TOASTERHIDETIME);
                      } else {
                        setValue(e.target.checked ? 1 : 0);
                        customPlanForm.patchValue({
                          TrialPeriodDays: e.target.checked ? '' : '',
                        });
                        customPlanForm.get('TrialPeriodDays').updateValueAndValidity();
                      }
                    }}
                  />
                  <label htmlFor="allow_trial_period">Allow Trial Period</label>
                </div>
              )}
            />
          </div>
          <div className="col-12 col-md-4">
            <FieldControl
              control={customPlanForm.get('AllowTrialPeriod')}
              render={({ value: allowTrialPeriodValue }) => (
                <>
                  <AmountContainer
                    key={allowTrialPeriodValue}
                    name="TrialPeriodDays"
                    hideLabel={true}
                    placeholder="Set Trial Period in days"
                    maxlength={11}
                    removeDefaultPlaceholder={true}
                    setMinValidation={true}
                    control={customPlanForm.get('TrialPeriodDays')}
                    isRequired={true}
                    disabled={disabled || !allowTrialPeriodValue || allowTrialPeriodValue === '0'}
                    className='m-0'
                    hideIcon
                    allowDecimal={false}
                    errorMessage="Trial period is required"
                  />
                  {
                    showTrialEndDetail && (
                      <FieldControl
                        key={customPlanForm.get('TrialPeriodDays')?.value}
                        control={customPlanForm.get('TrialPeriodDays')}
                        render={({ value: trialPeriodDays }) => {
                          const allowTrial = allowTrialPeriodValue === 1 || allowTrialPeriodValue === '1';
                          const daysNum = typeof trialPeriodDays === 'string' ? parseFloat(String(trialPeriodDays).replace(/,/g, '')) : Number(trialPeriodDays);
                          const showDate = allowTrial && !Number.isNaN(daysNum) && daysNum > 0;
                          return (
                            <span className="trial-tag ms-0">
                              {showDate ? `Trial ends on ${moment().add(daysNum, 'days').format('MMM D, YYYY')}` : 'No trial period'}
                            </span>
                          );
                        }}
                      />
                    )
                  }
                </>
              )}
            />
          </div>
        </div>
      </div>

      <h6 className="lm-card-sub-heading">Set Plan Limits</h6>

      {/* Limit Duration */}
      <div className="row mb-3 pl-1">
        <div className="col-md-3">
          <label className="mt-1" htmlFor="limit_duration">
            Limit Duration
          </label>
        </div>
        <div className="col-md-3">
          <FieldControl
            strict={false}
            control={customPlanForm.get('LimitDuration')}
            render={({ value, setValue, touched, hasError }) => {
              const showError = touched && hasError('required');
              return (
                <div className={`form-group ${showError ? 'error-field' : ''}`}>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    id="limit_duration"
                    options={limitDurationOptions}
                    isDisabled={disabled}
                    value={limitDurationOptions.find(
                      (opt) => opt.value?.toString() === value?.toString()
                    )}
                    onChange={(option) => {
                      if (disabled) return;
                      setValue(option ? option.value : 0)
                    }}
                  />
                  {showError && (
                    <span className="error-message">Limit duration is required</span>
                  )}
                </div>
              );
            }}
          />
        </div>
      </div>

      {/* No of Borrowers */}
      <div className="row mb-3 pl-1">
        <div className="col-md-3">
          <label className="mt-1" htmlFor="no_of_users">
            Number of Borrowers
          </label>
        </div>
        <div className="col-md-3">
          <FieldControl
            strict={false}
            control={customPlanForm.get('NoofBorrowers')}
            render={({ value, setValue, touched, hasError }) => {
              const showError = touched && hasError('required');
              return (
                <div className={`form-group ${showError ? 'error-field' : ''}`}>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    id="no_of_users"
                    options={unlimitedLimitedOptions}
                    isDisabled={disabled}
                    value={unlimitedLimitedOptions.find(
                      (opt) => opt.value?.toString() === value?.toString()
                    )}
                    onChange={(option) => {
                      if (disabled) return;
                      const newValue = option ? option.value : '';
                      setValue(newValue);
                      customPlanForm.patchValue({
                        NoofBorrowersLimit: '0',
                        BorrowersOverCharge: '0',
                      });
                      customPlanForm.get('NoofBorrowersLimit').updateValueAndValidity();
                      customPlanForm.get('BorrowersOverCharge').updateValueAndValidity();
                    }}
                  />
                  {showError && (
                    <span className="error-message">Number of borrowers is required</span>
                  )}
                </div>
              );
            }}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('NoofBorrowers')}
            render={({ value: noofBorrowersValue }) => (
              <AmountContainer
                name="noofBorrowersLimit"
                control={customPlanForm.get('NoofBorrowersLimit')}
                placeholder="Set Usage Limit"
                disabled={disabled || noofBorrowersValue === 0 || noofBorrowersValue === '0'}
                hideLabel={true}
                isRequired={true}
                removeDefaultPlaceholder={true}
                maxlength={11}
                hideIcon
                errorMessage="Usage limit is required"
                allowDecimal={false}
              />
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('NoofBorrowers')}
            render={({ value: noofBorrowersValue }) => (
              <AmountContainer
                name="borrowersOverCharge"
                control={customPlanForm.get('BorrowersOverCharge')}
                placeholder="Set Overage Charge (per user)"
                disabled={disabled || noofBorrowersValue === 0 || noofBorrowersValue === '0'}
                hideLabel={true}
                isRequired={true}
                removeDefaultPlaceholder={true}
                maxlength={11}
                errorMessage="Overage charge is required"
              />
            )}
          />
        </div>
      </div>

      {/* No of Partners */}
      <div className="row mb-3 pl-1">
        <div className="col-md-3">
          <label className="mt-1" htmlFor="no_of_partners">
            Number of Partners
          </label>
        </div>
        <div className="col-md-3">
          <FieldControl
            strict={false}
            control={customPlanForm.get('NoofPartners')}
            render={({ value, setValue, touched, hasError }) => {
              const showError = touched && hasError('required');
              return (
                <div className={`form-group ${showError ? 'error-field' : ''}`}>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    id="no_of_partners"
                    options={unlimitedLimitedOptions}
                    isDisabled={disabled}
                    value={unlimitedLimitedOptions.find(
                      (opt) => opt.value?.toString() === value?.toString()
                    )}
                    onChange={(option) => {
                      if (disabled) return;
                      const newValue = option ? option.value : '';
                      setValue(newValue);
                      customPlanForm.patchValue({
                        NoofPartnersLimit: '0',
                        PartnersOverCharge: '0',
                      });
                      customPlanForm.get('NoofPartnersLimit').updateValueAndValidity();
                      customPlanForm.get('PartnersOverCharge').updateValueAndValidity();
                    }}
                  />
                  {showError && (
                    <span className="error-message">Number of partners is required</span>
                  )}
                </div>
              );
            }}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('NoofPartners')}
            render={({ value: noofPartnersValue }) => (
              <AmountContainer
                name="noofPartnersLimit"
                control={customPlanForm.get('NoofPartnersLimit')}
                placeholder="Set Usage Limit"
                disabled={disabled || noofPartnersValue === 0 || noofPartnersValue === '0'}
                hideLabel={true}
                isRequired={true}
                removeDefaultPlaceholder={true}
                maxlength={11}
                hideIcon
                errorMessage="Usage limit is required"
                allowDecimal={false}
              />
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('NoofPartners')}
            render={({ value: noofPartnersValue }) => (
              <AmountContainer
                name="partnersOverCharge"
                control={customPlanForm.get('PartnersOverCharge')}
                placeholder="Set Overage Charge (per user)"
                disabled={disabled || noofPartnersValue === 0 || noofPartnersValue === '0'}
                hideLabel={true}
                isRequired={true}
                removeDefaultPlaceholder={true}
                maxlength={11}
                errorMessage="Overage charge is required"
              />
            )}
          />
        </div>
      </div>

      {/* No of other Users */}
      <div className="row mb-3 pl-1">
        <div className="col-md-3">
          <label className="mt-1" htmlFor="no_of_other_users">
            Number of other Users
          </label>
        </div>
        <div className="col-md-3">
          <FieldControl
            strict={false}
            control={customPlanForm.get('NoofUsers')}
            render={({ value, setValue, touched, hasError }) => {
              const showError = touched && hasError('required');
              return (
                <div className={`form-group ${showError ? 'error-field' : ''}`}>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    id="no_of_other_users"
                    options={unlimitedLimitedOptions}
                    isDisabled={disabled}
                    value={unlimitedLimitedOptions.find(
                      (opt) => opt.value?.toString() === value?.toString()
                    )}
                    onChange={(option) => {
                      if (disabled) return;
                      const newValue = option ? option.value : '';
                      setValue(newValue);
                      customPlanForm.patchValue({
                        NoofUsersLimit: '0',
                        UsersOverCharge: '0',
                      });
                      customPlanForm.get('NoofUsersLimit').updateValueAndValidity();
                      customPlanForm.get('UsersOverCharge').updateValueAndValidity();
                    }}
                  />
                  {showError && (
                    <span className="error-message">Number of other users is required</span>
                  )}
                </div>
              );
            }}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('NoofUsers')}
            render={({ value: noofUsersValue }) => (
              <AmountContainer
                name="noofUsersLimit"
                control={customPlanForm.get('NoofUsersLimit')}
                placeholder="Set Usage Limit"
                disabled={disabled || noofUsersValue === 0 || noofUsersValue === '0'}
                hideLabel={true}
                isRequired={true}
                removeDefaultPlaceholder={true}
                maxlength={11}
                hideIcon
                errorMessage="Usage limit is required"
                allowDecimal={false}
              />
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('NoofUsers')}
            render={({ value: noofUsersValue }) => (
              <AmountContainer
                name="usersOverCharge"
                control={customPlanForm.get('UsersOverCharge')}
                placeholder="Set Overage Charge (per user)"
                disabled={disabled || noofUsersValue === 0 || noofUsersValue === '0'}
                hideLabel={true}
                isRequired={true}
                removeDefaultPlaceholder={true}
                maxlength={11}
                errorMessage="Overage charge is required"
              />
            )}
          />
        </div>
      </div>

      {/* No of Transactions */}
      <div className="row mb-3 pl-1">
        <div className="col-md-3">
          <label className="mt-1" htmlFor="no_of_transactions">
            Number of Transactions
          </label>
        </div>
        <div className="col-md-3">
          <FieldControl
            strict={false}
            control={customPlanForm.get('NoofTransactions')}
            render={({ value, setValue, touched, hasError }) => {
              const showError = touched && hasError('required');
              return (
                <div className={`form-group ${showError ? 'error-field' : ''}`}>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    id="no_of_transactions"
                    options={unlimitedLimitedOptions}
                    isDisabled={disabled}
                    value={unlimitedLimitedOptions.find(
                      (opt) => opt.value?.toString() === value?.toString()
                    )}
                    onChange={(option) => {
                      if (disabled) return;
                      const newValue = option ? option.value : '';
                      setValue(newValue);
                      customPlanForm.patchValue({
                        NoofTransactionsLimit: '0',
                        TransactionsOverCharge: '0',
                      });
                      customPlanForm.get('NoofTransactionsLimit').updateValueAndValidity();
                      customPlanForm.get('TransactionsOverCharge').updateValueAndValidity();
                    }}
                  />
                  {showError && (
                    <span className="error-message">Number of transactions is required</span>
                  )}
                </div>
              );
            }}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('NoofTransactions')}
            render={({ value: noofTransactionsValue }) => (
              <AmountContainer
                name="noofTransactionsLimit"
                control={customPlanForm.get('NoofTransactionsLimit')}
                placeholder="Set Usage Limit"
                disabled={disabled || noofTransactionsValue === 0 || noofTransactionsValue === '0'}
                hideLabel={true}
                isRequired={true}
                removeDefaultPlaceholder={true}
                maxlength={11}
                hideIcon
                errorMessage="Usage limit is required"
                allowDecimal={false}
              />
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('NoofTransactions')}
            render={({ value: noofTransactionsValue }) => (
              <AmountContainer
                name="transactionsOverCharge"
                control={customPlanForm.get('TransactionsOverCharge')}
                placeholder="Set Overage Charge (per transaction)"
                disabled={disabled || noofTransactionsValue === 0 || noofTransactionsValue === '0'}
                hideLabel={true}
                isRequired={true}
                removeDefaultPlaceholder={true}
                maxlength={11}
                errorMessage="Overage charge is required"
              />
            )}
          />
        </div>
      </div>

      {/* MicroBilt: Credit Report */}
      <FieldControl
        strict={false}
        control={customPlanForm.get('APIName')}
        render={({ value, setValue, touched, hasError }) => (
          <div
            className={`form-group ${touched && hasError('required') ? 'error-field' : ''
              }`}
          >
            <div className="">
              <div className="form-check form-check-inline p-0">
                <input
                  type="radio"
                  className="form-check-input "
                  id="microbit"
                  name="APIName"
                  checked={value === 'microbit'}
                  disabled={disabled}
                  onChange={() => {
                    if (disabled) return;
                    setValue('microbit');
                  }}
                />
                <label className="form-check-label" htmlFor="microbit">
                  MicroBilt
                </label>
              </div>
              {/* <div className="form-check form-check-inline p-0">
                <input
                  type="radio"
                  className="form-check-input "
                  id="microbit_2"
                  name="APIName"
                  checked={value === 'microbit_2'}
                  onChange={() => setValue('microbit_2')}
                />
                <label className="form-check-label" htmlFor="microbit_2">
                  MicroBilt 2
                </label>
              </div>
              <div className="form-check form-check-inline p-0">
                <input
                  type="radio"
                  className="form-check-input "
                  id="microbit_3"
                  name="APIName"
                  checked={value === 'microbit_3'}
                  onChange={() => setValue('microbit_3')}
                />
                <label className="form-check-label" htmlFor="microbit_3">
                  MicroBilt 3
                </label>
              </div>
              <div className="form-check form-check-inline p-0">
                <input
                  type="radio"
                  className="form-check-input "
                  id="microbit_4"
                  name="APIName"
                  checked={value === 'microbit_4'}
                  onChange={() => setValue('microbit_4')}
                />
                <label className="form-check-label" htmlFor="microbit_4">
                  MicroBilt 4
                </label>
              </div> */}
            </div>
            {touched && hasError('required') && (
              <span className="error-message">
                Please select an API
              </span>
            )}
          </div>
        )}
      />
      <div className="row mb-3 pl-1">
        <div className="col-md-2">
          <label className="mt-1">
            Credit Report
          </label>
        </div>
        <div className="col-md-1">
          <FieldControl
            strict={false}
            control={customPlanForm.get('CreditReportStatus')}
            render={({ value, setValue }) => (
              <div className="lm-toggle-switch">
                <input
                  type="checkbox"
                  id="credit_report"
                  checked={value === 1}
                  disabled={disabled}
                  onChange={(e) => {
                    if (disabled) return;
                    setValue(e.target.checked ? 1 : 0);
                    customPlanForm.patchValue({
                      CreditReport: '',
                      CreditReportLimit: '',
                      CreditReportOverCharge: '',
                    });
                    customPlanForm.get('CreditReport').updateValueAndValidity();
                    customPlanForm.get('CreditReportLimit').updateValueAndValidity();
                    customPlanForm.get('CreditReportOverCharge').updateValueAndValidity();
                  }}
                />
                <label htmlFor="credit_report">Credit Report</label>
              </div>
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            strict={false}
            control={customPlanForm.get('CreditReport')}
            render={({ value, setValue, touched, hasError }) => {
              const enabled = customPlanForm.get('CreditReportStatus').value === 1;
              const showError = touched && hasError('required');
              return (
                <div className={`form-group ${showError ? 'error-field' : ''}`}>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    id="credit_report_usage"
                    options={unlimitedLimitedOptions}
                    isDisabled={disabled || !enabled}
                    value={unlimitedLimitedOptions.find(
                      (opt) => opt.value?.toString() === value?.toString()
                    ) || ''}
                    onChange={(option) => {
                      if (disabled) return;
                      const newValue = option ? option.value : '';
                      setValue(newValue);
                      customPlanForm.patchValue({
                        CreditReportLimit: '0',
                        CreditReportOverCharge: '0',
                      });
                      customPlanForm.get('CreditReportLimit').updateValueAndValidity();
                      customPlanForm.get('CreditReportOverCharge').updateValueAndValidity();
                    }}
                  />
                  {showError && (
                    <span className="error-message">
                      Credit report usage limit is required
                    </span>
                  )}
                </div>
              );
            }}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('CreditReportStatus')}
            render={({ value: creditReportValue }) => (
              <FieldControl
                control={customPlanForm.get('CreditReport')}
                render={({ value: creditReportStatusValue }) => (
                  <AmountContainer
                    name="creditReportLimit"
                    control={customPlanForm.get('CreditReportLimit')}
                    placeholder="Set Usage Limit"
                    disabled={
                      disabled ||
                      (creditReportValue !== 1 && creditReportValue !== '1') ||
                      creditReportStatusValue === 0 ||
                      creditReportStatusValue === '0'
                    }
                    hideLabel={true}
                    isRequired={true}
                    removeDefaultPlaceholder={true}
                    maxlength={11}
                    hideIcon
                    errorMessage="Usage limit is required"
                    allowDecimal={false}
                  />
                )}
              />
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('CreditReportStatus')}
            render={({ value: creditReportValue }) => (
              <FieldControl
                control={customPlanForm.get('CreditReport')}
                render={({ value: creditReportStatusValue }) => (
                  <AmountContainer
                    name="creditReportOverCharge"
                    control={customPlanForm.get('CreditReportOverCharge')}
                    placeholder="Set Overage Charge (per use)"
                    disabled={
                      disabled ||
                      (creditReportValue !== 1 && creditReportValue !== '1') ||
                      creditReportStatusValue === 0 ||
                      creditReportStatusValue === '0'
                    }
                    hideLabel={true}
                    isRequired={true}
                    removeDefaultPlaceholder={true}
                    maxlength={11}
                    errorMessage="Overage charge is required"
                  />
                )}
              />
            )}
          />
        </div>
      </div>

      {/* Third Party Reports */}
      <div className="row mb-3 pl-1">
        <div className="col-md-2">
          <label className="mt-0">
            Third Party Reports
          </label>
        </div>
        <div className="col-md-1">
          <FieldControl
            strict={false}
            control={customPlanForm.get('ThirdPartyReportsStatus')}
            render={({ value, setValue }) => (
              <div className="lm-toggle-switch">
                <input
                  type="checkbox"
                  id="third_party_reports"
                  checked={value === 1}
                  disabled={disabled}
                  onChange={(e) => {
                    if (disabled) return;
                    setValue(e.target.checked ? 1 : 0);
                    customPlanForm.patchValue({
                      ThirdPartyReports: '',
                      ThirdPartyReportsLimit: '',
                      ThirdPartyReportsOverCharge: '',
                    });
                    customPlanForm.get('ThirdPartyReports').updateValueAndValidity();
                    customPlanForm.get('ThirdPartyReportsLimit').updateValueAndValidity();
                    customPlanForm.get('ThirdPartyReportsOverCharge').updateValueAndValidity();
                  }}
                />
                <label htmlFor="third_party_reports">Third Party Reports</label>
              </div>
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            strict={false}
            control={customPlanForm.get('ThirdPartyReports')}
            render={({ value, setValue, touched, hasError }) => {
              const enabled = customPlanForm.get('ThirdPartyReportsStatus').value === 1;
              const showError = touched && hasError('required');
              return (
                <div className={`form-group ${showError ? 'error-field' : ''}`}>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    id="third_party_reports_usage"
                    options={unlimitedLimitedOptions}
                    isDisabled={disabled || !enabled}
                    value={unlimitedLimitedOptions.find(
                      (opt) => opt.value?.toString() === value?.toString()
                    ) || ''}
                    onChange={(option) => {
                      if (disabled) return;
                      const newValue = option ? option.value : '';
                      setValue(newValue);
                      customPlanForm.patchValue({
                        ThirdPartyReportsLimit: '0',
                        ThirdPartyReportsOverCharge: '0',
                      });
                      customPlanForm.get('ThirdPartyReportsLimit').updateValueAndValidity();
                      customPlanForm.get('ThirdPartyReportsOverCharge').updateValueAndValidity();
                    }}
                  />
                  {showError && (
                    <span className="error-message">
                      Third party reports usage limit is required
                    </span>
                  )}
                </div>
              );
            }}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('ThirdPartyReportsStatus')}
            render={({ value: thirdPartyReportsValue }) => (
              <FieldControl
                control={customPlanForm.get('ThirdPartyReports')}
                render={({ value: thirdPartyReportsStatusValue }) => (
                  <AmountContainer
                    name="thirdPartyReportsLimit"
                    control={customPlanForm.get('ThirdPartyReportsLimit')}
                    placeholder="Set Usage Limit"
                    disabled={
                      disabled ||
                      (thirdPartyReportsValue !== 1 && thirdPartyReportsValue !== '1') ||
                      thirdPartyReportsStatusValue === 0 ||
                      thirdPartyReportsStatusValue === '0'
                    }
                    hideLabel={true}
                    isRequired={true}
                    removeDefaultPlaceholder={true}
                    maxlength={11}
                    hideIcon
                    errorMessage="Usage limit is required"
                    allowDecimal={false}
                  />
                )}
              />
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('ThirdPartyReportsStatus')}
            render={({ value: thirdPartyReportsValue }) => (
              <FieldControl
                control={customPlanForm.get('ThirdPartyReports')}
                render={({ value: thirdPartyReportsStatusValue }) => (
                  <AmountContainer
                    name="thirdPartyReportsOverCharge"
                    control={customPlanForm.get('ThirdPartyReportsOverCharge')}
                    placeholder="Set Overage Charge (per use)"
                    disabled={
                      disabled ||
                      (thirdPartyReportsValue !== 1 && thirdPartyReportsValue !== '1') ||
                      thirdPartyReportsStatusValue === 0 ||
                      thirdPartyReportsStatusValue === '0'
                    }
                    hideLabel={true}
                    isRequired={true}
                    removeDefaultPlaceholder={true}
                    maxlength={11}
                    errorMessage="Overage charge is required"
                  />
                )}
              />
            )}
          />
        </div>
      </div>

      {/* HelloSign */}
      <div className="row pl-1">
        <div className="col-md-2">
          <h6 className="lm-card-sub-heading mt-1">HelloSign</h6>
        </div>
        <div className="col-md-1">
          <FieldControl
            strict={false}
            control={customPlanForm.get('HelloSignStatus')}
            render={({ value, setValue }) => (
              <div className="lm-toggle-switch">
                <input
                  type="checkbox"
                  id="HelloSign"
                  checked={value === 1}
                  disabled={disabled}
                  onChange={(e) => {
                    if (disabled) return;
                    setValue(e.target.checked ? 1 : 0);
                    customPlanForm.patchValue({
                      HelloSign: '',
                      HelloSignLimit: '',
                      HelloSignOverCharge: '',
                    });
                    customPlanForm.get('HelloSign').updateValueAndValidity();
                    customPlanForm.get('HelloSignLimit').updateValueAndValidity();
                    customPlanForm.get('HelloSignOverCharge').updateValueAndValidity();
                  }}
                />
                <label htmlFor="HelloSign">HelloSign</label>
              </div>
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            strict={false}
            control={customPlanForm.get('HelloSign')}
            render={({ value, setValue, touched, hasError }) => {
              const enabled = customPlanForm.get('HelloSignStatus').value === 1;
              const showError = touched && hasError('required');
              return (
                <div className={`form-group ${showError ? 'error-field' : ''}`}>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    id="hellosign_usage"
                    options={unlimitedLimitedOptions}
                    isDisabled={disabled || !enabled}
                    value={unlimitedLimitedOptions.find(
                      (opt) => opt.value?.toString() === value?.toString()
                    ) || ''}
                    onChange={(option) => {
                      if (disabled) return;
                      const newValue = option ? option.value : '';
                      setValue(newValue);
                      customPlanForm.patchValue({
                        HelloSignLimit: '0',
                        HelloSignOverCharge: '0',
                      });
                      customPlanForm.get('HelloSignLimit').updateValueAndValidity();
                      customPlanForm.get('HelloSignOverCharge').updateValueAndValidity();
                    }}
                  />
                  {showError && (
                    <span className="error-message">
                      HelloSign usage limit is required
                    </span>
                  )}
                </div>
              );
            }}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('HelloSignStatus')}
            render={({ value: helloSignValue }) => (
              <FieldControl
                control={customPlanForm.get('HelloSign')}
                render={({ value: helloSignStatusValue }) => (
                  <AmountContainer
                    name="helloSignLimit"
                    control={customPlanForm.get('HelloSignLimit')}
                    placeholder="Set Usage Limit"
                    disabled={
                      disabled ||
                      (helloSignValue !== 1 && helloSignValue !== '1') ||
                      helloSignStatusValue === 0 ||
                      helloSignStatusValue === '0'
                    }
                    hideLabel={true}
                    isRequired={true}
                    removeDefaultPlaceholder={true}
                    maxlength={11}
                    hideIcon
                    errorMessage="Usage limit is required"
                    allowDecimal={false}
                  />
                )}
              />
            )}
          />
        </div>
        <div className="col-md-3">
          <FieldControl
            control={customPlanForm.get('HelloSignStatus')}
            render={({ value: helloSignValue }) => (
              <FieldControl
                control={customPlanForm.get('HelloSign')}
                render={({ value: helloSignStatusValue }) => (
                  <AmountContainer
                    name="helloSignOverCharge"
                    control={customPlanForm.get('HelloSignOverCharge')}
                    placeholder="Set Overage Charge (per use)"
                    disabled={
                      disabled ||
                      (helloSignValue !== 1 && helloSignValue !== '1') ||
                      helloSignStatusValue === 0 ||
                      helloSignStatusValue === '0'
                    }
                    hideLabel={true}
                    isRequired={true}
                    removeDefaultPlaceholder={true}
                    maxlength={11}
                    errorMessage="Overage charge is required"
                  />
                )}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateCustomPlan;

